import React, { useState, useMemo } from "react";
import ReactDOM from "react-dom";
// ── FDA date + derivation helpers (moved from fdaGrouping.js) ──

const formatFdaDate = (dateStr) => {
    if (!dateStr) return "—";
    if (typeof dateStr === "string" && dateStr.length === 8) {
        const y = dateStr.slice(0, 4);
        const m = dateStr.slice(4, 6);
        const d = dateStr.slice(6, 8);
        return `${m}/${d}/${y}`;
    }
    return dateStr;
};

const deriveApprovalHistory = (documents = []) => {
    const merge = (type) => {
        const map = new Map();
        documents
            .filter((d) => d.submission_type === type)
            .forEach((d) => {
                const key = String(d.submission_number);
                if (!map.has(key)) {
                    map.set(key, {
                        submissionType: type,
                        submissionNumber: d.submission_number,
                        submissionLabel: `${type === "ORIG" ? "ORIG" : "SUPPL"}-${d.submission_number}`,
                        actionDate: d.document_date || null,
                        docs: [],
                    });
                }
                const row = map.get(key);
                if (!row.actionDate && d.document_date) row.actionDate = d.document_date;
                if (d.document_url && d.document_type) {
                    row.docs.push({ type: d.document_type, url: d.document_url });
                }
            });
        return Array.from(map.values()).sort(
            (a, b) => Number(b.submissionNumber) - Number(a.submissionNumber)
        );
    };
    return {
        original: merge("ORIG"),
        supplements: merge("SUPPL"),
    };
};

const deriveLabels = (documents = []) =>
    documents
        .filter((d) => d.document_type === "Label" && d.document_url)
        .sort((a, b) => (b.document_date || "").localeCompare(a.document_date || ""));

const ingredientSignature = (product) =>
    (product.active_ingredients || [])
        .map((ai) => (ai.name || "").trim().toUpperCase())
        .sort()
        .join(" + ");

const strengthLabel = (product) =>
    (product.active_ingredients || [])
        .map((ai) => ai.strength || "")
        .join("; ");

const deriveTherapeuticEquivalents = (selectedApp, allApplications = []) => {
    if (!selectedApp) return [];
    const buckets = [];
    (selectedApp.products || []).forEach((selProd) => {
        if (!selProd.te_code) return;
        const sig      = ingredientSignature(selProd);
        const form     = (selProd.dosage_form || "").toUpperCase();
        const route    = (selProd.route || "").toUpperCase();
        const teCode   = selProd.te_code;
        const strength = strengthLabel(selProd).toUpperCase();
        const rows = [];
        allApplications.forEach((app) => {
            (app.products || []).forEach((p) => {
                if (
                    ingredientSignature(p) === sig &&
                    (p.dosage_form || "").toUpperCase() === form &&
                    (p.route || "").toUpperCase() === route &&
                    strengthLabel(p).toUpperCase() === strength &&
                    p.te_code === teCode
                ) {
                    rows.push({
                        drugName: p.brand_name,
                        activeIngredients: (p.active_ingredients || []).map((ai) => ai.name).join("; "),
                        strength: strengthLabel(p),
                        dosageForm: `${p.dosage_form}; ${p.route}`,
                        marketingStatus: p.marketing_status,
                        rld: p.reference_drug,
                        applicationNumber: app.application_number,
                        company: app.sponsor_name,
                        isSelf: app.id === selectedApp.id,
                    });
                }
            });
        });
        if (rows.length > 0) {
            rows.sort((a, b) => {
                if (a.rld !== b.rld) return a.rld === "Yes" ? -1 : 1;
                return (a.company || "").localeCompare(b.company || "");
            });
            buckets.push({
                heading: `${selProd.dosage_form}; ${selProd.route}; ${strengthLabel(selProd)}`,
                teCode,
                rows,
            });
        }
    });
    return buckets;
};

const APPLICATION_TYPE_COLORS = {
    NDA: "ev-badge-blue",
    ANDA: "ev-badge-orange",
    BLA: "ev-badge-purple",
};

// Collapsible section shell — mirrors FDA's blue accordion panels
const Section = ({ title, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="discovery-section mb-2">
            <button
                type="button"
                className="discovery-section-header"
                onClick={() => setOpen((o) => !o)}
            >
                <span className="ev-small-p-dark ev-bold-med text-truncate">{title}</span>
                <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11 flex-shrink-0`} />
            </button>
            {open && <div className="discovery-section-body">{children}</div>}
        </div>
    );
};

// One submission's PDF links (Label / Letter / Review ...)
const DocLinks = ({ docs, onPreview }) => {
    if (!docs || docs.length === 0) return <span className="ev-xsmall-p-dark text-muted">No documents available</span>;
    return (
        <div className="d-flex flex-column gap-1">
            {docs.map((d, i) => (
                <div key={i} className="d-flex align-items-center gap-2">
                    <span className="ev-small-p-blue discovery-doc-type">{d.type} (PDF)</span>
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round ev-btn-xsmall d-flex align-items-center gap-1"
                        onClick={() => onPreview(d.url, d.type)}
                    >
                        View <i className="bi bi-eye" />
                    </button>
                </div>
            ))}
        </div>
    );
};

const FDAApplicationDetailPanel = ({ application, allApplications = [] }) => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewName, setPreviewName] = useState("");

    const history = useMemo(
        () => deriveApprovalHistory(application?.documents),
        [application]
    );
    const labels = useMemo(
        () => deriveLabels(application?.documents),
        [application]
    );
    const teGroups = useMemo(
        () => deriveTherapeuticEquivalents(application, allApplications),
        [application, allApplications]
    );

    if (!application) {
        return (
            <div className="ev-card-2 p-4 vh-55 d-flex flex-column align-items-center justify-content-center">
                <i className="bi bi-file-earmark-medical ev-fs-50 text-muted mb-3"></i>
                <p className="ev-reg-p-dark ev-bold-med mb-1">No Application Selected</p>
                <p className="ev-small-p-dark text-muted text-center mb-0">
                    Select an application from the list to view its details.
                </p>
            </div>
        );
    }

    const products = application.products || [];
    const appNo = application.application_number || "—";

    return (
        <div className="ev-card-2 p-3 pe-1 vh-55 d-flex flex-column position-relative">
            {/* Fixed header */}
            <div className="flex-shrink-0 pe-2">
                <div className="d-flex align-items-center gap-2 mb-1">
                    <span
                        className={`ev-badge-pill ev-fs-11 ${APPLICATION_TYPE_COLORS[application.application_type] || "ev-badge-blue"}`}
                    >
                        {application.application_type || "—"}
                    </span>
                    <p className="ev-bold-med ev-reg-p-dark mb-0">{appNo}</p>
                </div>
                <p className="ev-xsmall-p-dark text-muted mb-2">
                    Company: {application.sponsor_name || "—"}
                </p>
            </div>

            {/* Scrollable sections */}
            <div className="flex-grow-1 scrollable-element pe-2">
            {/* ── Products ── */}
            <Section title={`Products on ${appNo}`} defaultOpen={true}>
                {products.length === 0 ? (
                    <p className="ev-xsmall-p-dark text-muted mb-0">No products available.</p>
                ) : (
                    <div className="table-container">
                        <table className="table-custom1 w-100 mt-0">
                            <thead>
                                <tr>
                                    <th><label>Drug Name</label></th>
                                    <th><label>Active Ingredients</label></th>
                                    <th><label>Strength</label></th>
                                    <th><label>Dosage Form/Route</label></th>
                                    <th><label>Marketing Status</label></th>
                                    <th><label>TE Code</label></th>
                                    <th><label>RLD</label></th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p, idx) => (
                                    <tr key={idx}>
                                        <td className="ev-small-p-dark ev-bold-med">{p.brand_name || "—"}</td>
                                        <td className="ev-small-p-dark">
                                            {(p.active_ingredients || []).map((ai) => ai.name).join("; ") || "—"}
                                        </td>
                                        <td className="ev-small-p-dark">
                                            {(p.active_ingredients || []).map((ai) => ai.strength).join("; ") || "—"}
                                        </td>
                                        <td className="ev-small-p-dark">
                                            {p.dosage_form || "—"}{p.route ? `; ${p.route}` : ""}
                                        </td>
                                        <td className="ev-small-p-dark">{p.marketing_status || "—"}</td>
                                        <td className="ev-small-p-dark">{p.te_code || "None"}</td>
                                        <td className="ev-small-p-dark">{p.reference_drug || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            {/* ── Approval Date(s) and History ── */}
            <Section title={`Approval Date(s) and History, Letters, Labels, Reviews for ${appNo}`}>
                <p className="ev-xsmall-p-dark ev-bold-med mb-1">Original Approvals or Tentative Approvals</p>
                {history.original.length === 0 ? (
                    <p className="ev-xsmall-p-dark text-muted mb-2">No original approval records.</p>
                ) : (
                    <div className="table-container mb-3">
                        <table className="table-custom1 discovery-history-table mt-0">
                            <thead>
                                <tr>
                                    <th className="discovery-table-date"><label>Action Date</label></th>
                                    <th className="discovery-table-submission"><label>Submission</label></th>
                                    <th className="discovery-table-docs"><label>Letters, Reviews, Labels</label></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.original.map((row, i) => (
                                    <tr key={i}>
                                        <td className="ev-small-p-dark discovery-table-date">{formatFdaDate(row.actionDate)}</td>
                                        <td className="ev-small-p-dark discovery-table-submission">{row.submissionLabel}</td>
                                        <td className="discovery-table-docs"><DocLinks docs={row.docs} onPreview={(url, name) => { setPreviewUrl(url); setPreviewName(name); }} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <p className="ev-xsmall-p-dark ev-bold-med mb-1">Supplements</p>
                {history.supplements.length === 0 ? (
                    <p className="ev-xsmall-p-dark text-muted mb-0">No supplement records.</p>
                ) : (
                    <div className="table-container">
                        <table className="table-custom1 discovery-history-table mt-0">
                            <thead>
                                <tr>
                                    <th className="discovery-table-date"><label>Action Date</label></th>
                                    <th className="discovery-table-submission"><label>Submission</label></th>
                                    <th className="discovery-table-docs"><label>Letters, Reviews, Labels</label></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.supplements.map((row, i) => (
                                    <tr key={i}>
                                        <td className="ev-small-p-dark discovery-table-date">{formatFdaDate(row.actionDate)}</td>
                                        <td className="ev-small-p-dark discovery-table-submission">{row.submissionLabel}</td>
                                        <td className="discovery-table-docs"><DocLinks docs={row.docs} onPreview={(url, name) => { setPreviewUrl(url); setPreviewName(name); }} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            {/* ── Labels ── */}
            <Section title={`Labels for ${appNo}`}>
                {labels.length === 0 ? (
                    <p className="ev-xsmall-p-dark text-muted mb-0">No labels available on this site.</p>
                ) : (
                    <div className="table-container">
                        <table className="table-custom1 w-100 mt-0">
                            <thead>
                                <tr>
                                    <th><label>Action Date</label></th>
                                    <th><label>Submission</label></th>
                                    <th><label>Label</label></th>
                                </tr>
                            </thead>
                            <tbody>
                                {labels.map((d) => (
                                    <tr key={d.id}>
                                        <td className="ev-small-p-dark">{formatFdaDate(d.document_date)}</td>
                                        <td className="ev-small-p-dark">
                                            {d.submission_type === "ORIG" ? "ORIG" : "SUPPL"}-{d.submission_number}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="ev-small-p-blue text-nowrap">Label (PDF)</span>
                                                <button
                                                    type="button"
                                                    className="ev-btn-lightblue-fill-round ev-btn-xsmall d-flex align-items-center gap-1"
                                                    onClick={() => { setPreviewUrl(d.document_url); setPreviewName("Label"); }}
                                                >
                                                    View <i className="bi bi-eye" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            {/* ── Therapeutic Equivalents (matched within current results) ── */}
            <Section title={`Therapeutic Equivalents for ${appNo}`}>
                {teGroups.length === 0 ? (
                    <p className="ev-xsmall-p-dark text-muted mb-0">
                        No therapeutic equivalents found in the current results.
                    </p>
                ) : (
                    teGroups.map((bucket, bi) => (
                        <div key={bi} className="mb-3">
                            <p className="ev-xsmall-p-dark ev-bold-med mb-0">{bucket.heading}</p>
                            <p className="ev-xsmall-p-dark text-muted mb-1">TE Code = {bucket.teCode}</p>
                            <div className="table-container">
                                <table className="table-custom1 w-100 mt-0">
                                    <thead>
                                        <tr>
                                            <th><label>Drug Name</label></th>
                                            <th><label>Strength</label></th>
                                            <th><label>Marketing Status</label></th>
                                            <th><label>RLD</label></th>
                                            <th><label>Application No.</label></th>
                                            <th><label>Company</label></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bucket.rows.map((r, ri) => (
                                            <tr key={ri} className={r.isSelf ? "discovery-row-active" : ""}>
                                                <td className="ev-small-p-dark ev-bold-med">{r.drugName || "—"}</td>
                                                <td className="ev-small-p-dark">{r.strength || "—"}</td>
                                                <td className="ev-small-p-dark">{r.marketingStatus || "—"}</td>
                                                <td className="ev-small-p-dark">{r.rld || "—"}</td>
                                                <td className="ev-small-p-dark">{r.applicationNumber || "—"}</td>
                                                <td className="ev-small-p-dark">{r.company || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))
                )}
            </Section>
            </div>
        {!!previewUrl && ReactDOM.createPortal(
            <div className="modal d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                    <div className="modal-content ev-card-3">
                        <div className="row modal-header border-0">
                            <div className="col-10">
                                <h6 className="ev-message-card3 m-0">{previewName || "Document Preview"}</h6>
                            </div>
                            <div className="col-2 text-end d-flex align-items-center justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn ev-btn-lightblue-fill-round ev-radius-xsmall ev-btn-med avatar-tooltip"
                                    data-tooltip="Open in New Tab"
                                    onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                                >
                                    <i className="bi bi-box-arrow-up-right"></i>
                                </button>
                                <button type="button" className="ev-btn-text-only-fill ev-fs-20" onClick={() => { setPreviewUrl(null); setPreviewName(""); }}>
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                        </div>
                        <div className="modal-body p-0 ev-radius-small vh-80">
                            <iframe
                                src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                                title={previewName || "Document Preview"}
                                width="100%"
                                height="100%"
                                style={{ border: "none", minHeight: "75vh" }}
                            />
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )}
        </div>
    );
};

export default FDAApplicationDetailPanel;